import type { TFunction } from "i18next";
import { CSSProperties, ReactNode } from "react";

import {
  CourseRouteTypeEnum,
  durationInMinutesCeil,
  ICourseDto,
  ICourseScheduleMetricItem,
  NO_VALUE_DISPLAY,
} from "@ryde/shared";

import { getCourseInfoTranslationKey } from "../../grid/cell-templates/rides/summary/course.summary.helpers";

const tooltipTitleStyle: CSSProperties = {
  fontSize: "13px",
  fontWeight: 500,
  margin: "0 0 3px",
};

const tooltipDetailStyle: CSSProperties = {
  fontSize: "11px",
  fontWeight: 300,
  margin: 0,
};

const tooltipMetaStyle: CSSProperties = {
  fontSize: "11px",
  fontWeight: 400,
  margin: "4px 0 0",
  opacity: 0.9,
};

export interface ICourseRouteTypeTooltipData {
  courseId: number;
  courseName?: string;
  routeType?: CourseRouteTypeEnum;
  courseTotalDuration?: number;
  courseStationsCount?: number;
}

export const resolveScheduleRouteType = (
  schedule: ICourseScheduleMetricItem,
  course?: ICourseDto,
): CourseRouteTypeEnum | undefined => schedule.routeType ?? course?.routeType;

export const buildCourseRouteSummaryLabel = ({
  t,
  courseTotalDuration = 0,
  courseStationsCount = 0,
}: {
  t: TFunction;
  courseTotalDuration?: number;
  courseStationsCount?: number;
}): string | null => {
  const minutes =
    courseTotalDuration > 0 ? durationInMinutesCeil(courseTotalDuration) : null;
  const stationsCount = courseStationsCount;

  if (!minutes && !stationsCount) {
    return null;
  }

  const stationsWord =
    stationsCount === 1 ? t("station.single") : t("station.plural");

  if (minutes && stationsCount) {
    return `${minutes}min/${stationsCount} ${stationsWord}`;
  }

  if (minutes) {
    return `${minutes}min`;
  }

  return `${stationsCount} ${stationsWord}`;
};

export interface ICourseRouteTypeInlineLabels {
  routeTypeLabel: string;
  detailText?: string;
  summaryLabel: string | null;
}

/** Plain-text labels for inline course route info (dialogs where tooltips clip). */
export const buildCourseRouteTypeInlineLabels = ({
  t,
  courseId,
  courseName,
  routeType,
  courseTotalDuration,
  courseStationsCount,
}: ICourseRouteTypeTooltipData & {
  t: TFunction;
}): ICourseRouteTypeInlineLabels => {
  const summaryLabel = buildCourseRouteSummaryLabel({
    t,
    courseTotalDuration,
    courseStationsCount,
  });

  if (!routeType) {
    const trimmedName = courseName?.trim() || "";
    const courseLine = trimmedName
      ? `${courseId}: ${trimmedName}`
      : String(courseId);
    return {
      routeTypeLabel: `${NO_VALUE_DISPLAY} | ${courseLine}`,
      summaryLabel,
    };
  }

  const shortLabel = t(`selectListLabel.${routeType}`);
  const detailKey = getCourseInfoTranslationKey(routeType);

  return {
    routeTypeLabel: shortLabel,
    detailText: t(detailKey),
    summaryLabel,
  };
};

/** Shared course-type tooltip: title + detail + optional duration/stations (calendar-style). */
export const buildCourseRouteTypeTooltipContent = ({
  t,
  courseId,
  courseName,
  routeType,
  courseTotalDuration,
  courseStationsCount,
}: ICourseRouteTypeTooltipData & { t: TFunction }): ReactNode => {
  const trimmedName = courseName?.trim() || "";
  const courseLine = trimmedName
    ? `${courseId}: ${trimmedName}`
    : String(courseId);
  const routeSummary = buildCourseRouteSummaryLabel({
    t,
    courseTotalDuration,
    courseStationsCount,
  });
  const routeSummaryLine = routeSummary ? (
    <p style={tooltipMetaStyle}>{routeSummary}</p>
  ) : null;

  if (!routeType) {
    return (
      <>
        <span style={tooltipTitleStyle}>
          {NO_VALUE_DISPLAY} | {courseLine}
        </span>
        {routeSummaryLine}
      </>
    );
  }

  const shortLabel = t(`selectListLabel.${routeType}`);
  const detailKey = getCourseInfoTranslationKey(routeType);

  return (
    <>
      <p style={tooltipTitleStyle}>{`${shortLabel} | ${courseLine}`}</p>
      <p style={tooltipDetailStyle}>{t(detailKey)}</p>
      {routeSummaryLine}
    </>
  );
};

export const buildCourseScheduleRouteTypeTooltip = ({
  t,
  schedule,
  course,
}: {
  t: TFunction;
  schedule: ICourseScheduleMetricItem;
  course?: ICourseDto;
}): ReactNode =>
  buildCourseRouteTypeTooltipContent({
    t,
    courseId: schedule.courseId,
    courseName: course?.name,
    routeType: resolveScheduleRouteType(schedule, course),
    courseTotalDuration: course?.metadata?.totalDuration,
    courseStationsCount: course?.metadata?.stationsInfo?.length,
  });
